import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import "./Vozvrat.css";
import { useSklad } from "../SkladContext";

/* ---------- Helpers ---------- */
const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/\s/g, "").replace(/,/g, ".");
  const x = Number(s);
  return Number.isFinite(x) ? x : 0;
};
const money = (n) =>
  `${toNum(n).toLocaleString("uz-UZ", { maximumFractionDigits: 3 })} so'm`;
const somMoney = (n) =>
  `${Math.round(toNum(n)).toLocaleString("uz-UZ")} so'm`;
// Do'kon valyutasiga qarab format: dollar sentlar bilan, so'm butun
const fmtCur = (n, currency) =>
  currency === "usd"
    ? `${toNum(n).toLocaleString("uz-UZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`
    : somMoney(n);
const asArr = (res) =>
  Array.isArray(res?.data) ? res.data : res?.data?.results || [];
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export default function Vozvrat() {
  /* ---------- Haridor qidirish ---------- */
  const [sellerQuery, setSellerQuery] = useState("");
  const [sellerOptions, setSellerOptions] = useState([]);
  const [seller, setSeller] = useState(null);
  const sellerSearchTimer = useRef(null);

  /* ---------- Rejim ---------- */
  const [mode, setMode] = useState("returned"); // ‘returned’ | ‘sold’

  /* ---------- Mobile tab ---------- */
  const [mobileTab, setMobileTab] = useState("info"); // ‘info’ | ‘action’

  /* ---------- Filtrlar (sana default bo’sh) ---------- */
  const { activeSklad } = useSklad();
  const [leftSearch, setLeftSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [leftSklad, setLeftSklad] = useState(activeSklad);
  useEffect(() => { setLeftSklad(activeSklad); }, [activeSklad]);

  /* ---------- Chap panel ro’yxati ---------- */
  const [leftList, setLeftList] = useState([]);
  const [loadingLeft, setLoadingLeft] = useState(false);
  const leftFetchTimer = useRef(null);

  /* ---------- O’ng yuqori: tanlangan sotilganlar (qaytarish uchun) ---------- */
  // item: {id, product_id, name, seria, unitPrice, maxQty, qty, condition}
  const [selectedSold, setSelectedSold] = useState([]);

  /* ---------- O’ng pastki: almashtirish savati ---------- */
  const [repWarehouse, setRepWarehouse] = useState(activeSklad);
  useEffect(() => { setRepWarehouse(activeSklad); }, [activeSklad]);
  const [warehouses, setWarehouses] = useState([]);
  const [qRep, setQRep] = useState("");
  const [repOpts, setRepOpts] = useState([]); // qidiruvdan chiqqan variantlar
  const [replaceItems, setReplaceItems] = useState([]); // tanlangan almashtirishlar
  const prodTimer = useRef(null);

  /* ---------- Statistika (haridor bo‘yicha qaytarilganlar) ---------- */
  const [retStats, setRetStats] = useState({ count: 0, amount: 0 });
  const [settleMethod, setSettleMethod] = useState("balance"); // balance | cash
  const [cashRefundType, setCashRefundType] = useState("cash_som");
  const [cashInType, setCashInType] = useState("cash_som");

  const refundTotal = useMemo(
    () => selectedSold.reduce((s, x) => s + toNum(x.qty) * toNum(x.unitPrice), 0),
    [selectedSold]
  );
  const replaceTotal = useMemo(
    () => replaceItems.reduce((s, x) => s + toNum(x.qty) * toNum(x.price), 0),
    [replaceItems]
  );
  const diffTotal = useMemo(
    () => toNum(refundTotal) - toNum(replaceTotal),
    [refundTotal, replaceTotal]
  );
  // Vozvrat do'kon valyutasi: exchange bo'lsa almashtirish skladi, aks holda
  // qaytariladigan tovar skladi; aniqlab bo'lmasa tanlangan tovar valyutasi.
  const skladCurrency = useMemo(() => {
    const byId = (id) => {
      const w = warehouses.find((x) => String(x.id) === String(id));
      return (w?.currency_mode === "usd") ? "usd" : "som";
    };
    const primary = replaceItems.length > 0 ? repWarehouse : leftSklad;
    if (primary && primary !== "all") return byId(primary);
    return selectedSold[0]?.currency === "usd" ? "usd" : "som";
  }, [warehouses, leftSklad, repWarehouse, replaceItems, selectedSold]);

  // Dollar do'konda o'nlik saqlanadi, so'mda butunga yaxlitlanadi
  const roundCur = (x) => (skladCurrency === "usd" ? Math.round(toNum(x) * 100) / 100 : Math.round(toNum(x)));
  const refundAmt = roundCur(Math.max(0, toNum(diffTotal)));   // mijozga qaytadigan
  const payAmt = roundCur(Math.max(0, -toNum(diffTotal)));     // mijoz to'laydigan

  // 🔴 Har bir tanlangan satrda condition tanlangan bo'lishi shart
  const allConditionsChosen = useMemo(
    () =>
      selectedSold.length > 0 &&
      selectedSold.every(
        (x) => x.condition === "yaroqli" || x.condition === "yaroqsiz"
      ),
    [selectedSold]
  );

  const canReturn =
    selectedSold.length > 0 && replaceItems.length === 0 && allConditionsChosen;

  const canExchange =
    selectedSold.length > 0 && replaceItems.length > 0 && allConditionsChosen;

  /* ========== Haridor qidirish (debounce) ========== */
  useEffect(() => {
    const q = sellerQuery.trim();
    if (!q) {
      setSellerOptions([]);
      return;
    }
    clearTimeout(sellerSearchTimer.current);
    sellerSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("sotuvchi/", {
          params: { search: q, ordering: "ismi" },
        });
        setSellerOptions(asArr({ data }));
      } catch {
        setSellerOptions([]);
      }
    }, 300);
  }, [sellerQuery]);

  /* ========== Haridor tanlanganda statistikalar ========== */
  useEffect(() => {
    if (!seller?.id) {
      setRetStats({ count: 0, amount: 0 });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("vozvrat/returned-items/", {
          params: { seller: seller.id },
        });
        const count = (data || []).reduce((s, r) => s + toNum(r.qty), 0);
        const amount = (data || []).reduce(
          (s, r) => s + toNum(r.qty) * toNum(r.unit_price),
          0
        );
        setRetStats({ count, amount });
      } catch {
        setRetStats({ count: 0, amount: 0 });
      }
    })();
  }, [seller]);

  /* ========== Omborlar ========== */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("sklad/");
        const W = asArr({ data }).map((w) => ({
          id: String(w.id),
          name: w.nom || w.name || "",
          currency_mode: w.currency_mode || "som",
        }));
        setWarehouses([{ id: "all", name: "Barchasi" }, ...W]);
      } catch {
        setWarehouses([{ id: "all", name: "Barchasi" }]);
      }
    })();
  }, []);

  useEffect(() => {
    if (settleMethod !== "cash") setCashRefundType("cash_som");
  }, [settleMethod]);
  useEffect(() => {
    if (settleMethod !== "cash") setCashInType("cash_som");
  }, [settleMethod]);
  // Dollar do'konda kassa faqat naqd - karta/terminal yo'q
  useEffect(() => {
    if (skladCurrency === "usd") {
      setCashRefundType("cash_som");
      setCashInType("cash_som");
    }
  }, [skladCurrency]);

  /* ========== Chap ro‘yxat ========== */
  const fetchLeftList = async () => {
    if (mode === "sold" && !seller?.id) {
      setLeftList([]);
      return;
    }
    setLoadingLeft(true);
    try {
      if (mode === "returned") {
        const { data } = await api.get("vozvrat/returned-items/", {
          params: {
            seller: seller?.id || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            search: leftSearch || undefined,
            sklad: leftSklad !== "all" ? leftSklad : undefined,
          },
        });
        const rows = (data || []).map((x) => ({
          kind: "returned",
          id: x.id,
          name: x.name,
          seria: x.seria,
          date: String(x.date || "").slice(0, 10),
          unitPrice: toNum(x.unit_price),
          qty: toNum(x.qty),
          status: x.condition === "yaroqsiz" ? "Yaroqsiz" : "Yaroqli",
          type: "—",
        }));
        setLeftList(rows);
      } else {
        const { data } = await api.get("sotilgan-maxsulot/", {
          params: {
            seller_ids: seller.id,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            search: leftSearch || undefined,
            ordering: "-created_at",
            sklad: leftSklad !== "all" ? leftSklad : undefined,
          },
        });

        const arr = asArr({ data });
        const rows = [];

        for (const r of arr) {
          const soldId = r.id;
          const remaining = toNum(r.sotilgansoni); // DB dagi hozirgi sotilgan son

          // agar hammasi qaytarilgan bo‘lsa, listga qo‘shmaymiz
          if (remaining <= 0) continue;

          rows.push({
            kind: "sold",
            id: soldId,
            product_id: r.maxsulot,
            name: r.maxsulot_nom || r.maxsulot?.nom,
            seria: r.maxsulot_seriyasi || r.maxsulot?.seriyasi,
            date: String(r.created_at || "").slice(0, 10),
            unitPrice: toNum(r.narx),
            currency: r.currency || "som",
            qty: remaining,
            status: r.tolov_turi === "nasiya" ? "Nasiya" : "To‘langan",
            type: r.tolov_turi,
          });
        }

        setLeftList(rows);
      }
    } catch {
      setLeftList([]);
    } finally {
      setLoadingLeft(false);
    }
  };

  useEffect(() => {
    clearTimeout(leftFetchTimer.current);
    leftFetchTimer.current = setTimeout(fetchLeftList, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller, mode, leftSearch, dateFrom, dateTo, leftSklad]);

  /* ========== Sotilganlarni tanlash (qty 1 dan) ========== */
  const togglePickSold = (row) => {
    if (mode !== "sold") return;
    setSelectedSold((prev) => {
      const has = prev.find((p) => p.id === row.id);
      if (has) return prev.filter((p) => p.id !== row.id);
      return [
        ...prev,
        {
          id: row.id,
          product_id: row.product_id,
          name: row.name,
          seria: row.seria,
          unitPrice: row.unitPrice,
          maxQty: toNum(row.qty),
          qty: 1, // boshlang'ich miqdor
          condition: "", // boshlang'ich holat – tanlanmagan
        },
      ];
    });
  };

  const incSelQty = (id, d) =>
    setSelectedSold((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, qty: clamp(toNum(p.qty) + d, 0.001, p.maxQty || 1) } : p
      )
    );

  // O'nlik miqdorni qo'lda kiritish (metrli/kg tovar: 1.5, 0.25)
  const setSelQtyStr = (id, raw) =>
    setSelectedSold((prev) =>
      prev.map((p) => (p.id === id ? { ...p, qty: raw } : p))
    );
  const blurSelQty = (id) =>
    setSelectedSold((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, qty: clamp(toNum(p.qty) || 0.001, 0.001, p.maxQty || 1) } : p
      )
    );

  const removeSel = (id) =>
    setSelectedSold((prev) => prev.filter((p) => p.id !== id));

  /* ========== Almashtirish qidiruvi (sklad bilan) ========== */
  useEffect(() => {
    const q = qRep.trim();
    if (!q) {
      setRepOpts([]);
      return;
    }
    clearTimeout(prodTimer.current);
    prodTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set("search", q);
        params.set("ordering", "-id");
        if (repWarehouse !== "all") params.set("sklad", String(repWarehouse));
        const { data } = await api.get(`maxsulot/?${params.toString()}`);
        const arr = asArr({ data });
        const rows = arr.map((m) => {
          const donaga = (m.narxlar || []).find(
            (n) => String(n.sarxturi || "").toLowerCase() === "donaga"
          );
          const unit = donaga?.narx ?? m.tan_narxi ?? 0;
          return { id: m.id, name: m.nom, seria: m.seriyasi, price: toNum(unit) };
        });
        setRepOpts(rows);
      } catch {
        setRepOpts([]);
      }
    }, 300);
  }, [qRep, repWarehouse]);

  const addReplace = (prod) =>
    setReplaceItems((prev) =>
      prev.find((p) => p.id === prod.id)
        ? prev
        : [
            ...prev,
            {
              id: prod.id,
              name: prod.name,
              seria: prod.seria,
              qty: 1,
              price: String(toNum(prod.price)),
            },
          ]
    );

  const changeRepQty = (id, d) =>
    setReplaceItems((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, qty: Math.max(0.001, toNum(p.qty) + d) } : p
      )
    );
  const setRepQtyStr = (id, raw) =>
    setReplaceItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, qty: raw } : p))
    );
  const blurRepQty = (id) =>
    setReplaceItems((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, qty: Math.max(0.001, toNum(p.qty) || 0.001) } : p
      )
    );

  const changeRepPrice = (id, val) =>
    setReplaceItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, price: val } : p))
    );

  const removeRep = (id) =>
    setReplaceItems((prev) => prev.filter((p) => p.id !== id));

  /* ========== Payload + Preview ========== */
  const buildPayload = (modeKind) => {
    // Valyuta va kassa aynan shu skladdan aniqlanadi:
    // exchange -> almashtirish skladi, refund -> qaytariladigan tovar skladi
    const skladForTx =
      modeKind === "exchange"
        ? (repWarehouse !== "all" ? Number(repWarehouse) : undefined)
        : (leftSklad !== "all" ? Number(leftSklad) : undefined);
    return {
    mode: modeKind, // 'refund' | 'exchange'
    sotuvchi: seller?.id,
    sklad: skladForTx,
    currency: skladCurrency,
    settle_method: settleMethod,
    cash_refund_type:
      settleMethod === "cash"
        ? diffTotal >= 0
          ? cashRefundType
          : cashInType
        : undefined,
    returned: selectedSold.map((s) => ({
      product_id: s.product_id,
      qty: toNum(s.qty),
      unit_price: s.unitPrice,
      sold_ref_id: s.id,
      condition: s.condition, // yaroqli / yaroqsiz
    })),
    replaced:
      modeKind === "exchange"
        ? replaceItems.map((p) => ({
            product_id: p.id,
            qty: toNum(p.qty),
            unit_price: toNum(p.price),
          }))
        : [],
    };
  };

  const preview = async (modeKind) => {
    try {
      const { data } = await api.post("vozvrat/preview/", buildPayload(modeKind));
      return data;
    } catch {
      return null;
    }
  };

  const [busy, setBusy] = useState(false);

  /* ========== Tasdiqlash (Qaytarish) ========== */
  const doReturn = async () => {
    if (!seller?.id || busy) return;
    if (!canReturn) {
      alert("Har bir tanlangan mahsulot uchun yaroqli/yaroqsiz holatini tanlang.");
      return;
    }
    try {
      setBusy(true);
      await preview("refund"); // ixtiyoriy tekshiruv
      await api.post("vozvrat/", buildPayload("refund"));

      // serverdan qayta o‘qib olamiz (sotilganlar endi kamaygan bo‘ladi)
      await fetchLeftList();

      // Tanlovni tozalash
      setSelectedSold([]);

      alert("Qaytarish yakunlandi.");
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.log("REFUND ERR", status, data);

      let msg = "Qaytarishda xatolik.";
      if (data?.detail) {
        msg += " " + data.detail;
      } else if (data) {
        msg += " " + JSON.stringify(data);
      }

      alert(msg + " (sold_ref/qty yoki ombor/balansni tekshiring)");
    } finally {
      setBusy(false);
    }
  };

  /* ========== Tasdiqlash (Almashtirish) ========== */
  const doExchange = async () => {
    if (!canExchange || !seller?.id || busy) return;
    try {
      setBusy(true);
      await preview("exchange");
      await api.post("vozvrat/", buildPayload("exchange"));

      // serverdan qayta o‘qish
      await fetchLeftList();

      // Tanlovlarni tozalash
      setSelectedSold([]);
      setReplaceItems([]);

      alert("Almashtirish yakunlandi.");
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.log("EXCHANGE ERR", status, data);

      let msg = "Almashtirishda xatolik.";
      if (data?.detail) {
        msg += " " + data.detail;
      } else if (data) {
        msg += " " + JSON.stringify(data);
      }

      alert(msg + " (sold_ref/qty yoki ombor yetarliligini tekshiring)");
    } finally {
      setBusy(false);
    }
  };

  /* ========== Balans chip (vozvrat do'kon valyutasidagi balans) ========== */
  const balanceChip = (() => {
    const b = skladCurrency === "usd"
      ? toNum(seller?.qarhaq_usd)
      : toNum(seller?.qarhaq);
    if (b > 0) return { cls: "green", txt: `Haqdorligi: ${fmtCur(b, skladCurrency)}` };
    if (b < 0) return { cls: "red", txt: `Qarzdorligi: ${fmtCur(Math.abs(b), skladCurrency)}` };
    return { cls: "gray", txt: "Holati: neytral" };
  })();

  return (
    <div className="vozvrat-wrap">
      {/* ======= HEADER ======= */}
      <div className="voz-head fr-panel">
        {/* Haridor qidiruv */}
        <div className="seller-search">
          <input
            className="voz-input"
            placeholder="Haridor ismi yoki telefon raqami bilan qidiring."
            value={sellerQuery}
            onChange={(e) => setSellerQuery(e.target.value)}
          />
          {sellerOptions.length > 0 && (
            <div className="dropdown">
              {sellerOptions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSeller(s);
                    setSellerQuery("");
                    setSellerOptions([]);
                  }}
                >
                  <div className="line1">{s.ismi}</div>
                  <div className="line2">
                    {[s.telraqam1, s.telraqam2].filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Rejim tugmalari */}
        <div className="mode">
          <button
            className={`voz-btn ${mode === "returned" ? "active" : ""}`}
            onClick={() => setMode("returned")}
            title="Qaytarilgan tovarlar"
            disabled={busy}
          >
            Qaytarilgan tovarlar
          </button>
          <button
            className={`voz-btn ${mode === "sold" ? "active" : ""}`}
            onClick={() => setMode("sold")}
            title="Sotilgan tovarlar"
            disabled={busy}
          >
            Sotilgan tovarlar
          </button>
        </div>

        {/* Haridor meta */}
        <div className="voz-seller">
          <div className="meta">
            <div className="name">{seller?.ismi || "—"}</div>
            <div className="phones">
              {seller?.telraqam1 || "—"}
              <br />
              {seller?.telraqam2 || ""}
            </div>
          </div>
          <img
            className="voz-avatar"
            src={
              seller?.mijoz_rasmi_url ||
              seller?.pasport_rasmi_url ||
              "https://cdn-icons-png.flaticon.com/512/706/706807.png"
            }
            alt="avatar"
          />
        </div>
      </div>

      {/* ======= SUMMARY CHIPS ======= */}
      <div className="voz-summary fr-panel">
        <div className={`voz-chip ${balanceChip.cls}`}>{balanceChip.txt}</div>
        <div className="voz-chip yellow">
          Qaytarilgan tovarlar soni: {retStats.count} dona
        </div>
        <div className="voz-chip blue">
          Qaytarilgan tovarlar umumiy narxi: {fmtCur(retStats.amount, skladCurrency)}
        </div>
      </div>

      {/* ======= MOBILE TABS ======= */}
      <div className="voz-mob-tabs">
        <button
          className={`voz-mob-tab${mobileTab === "info" ? " active" : ""}`}
          onClick={() => setMobileTab("info")}
        >
          Haridor & Mahsulot
        </button>
        <button
          className={`voz-mob-tab${mobileTab === "action" ? " active" : ""}`}
          onClick={() => setMobileTab("action")}
        >
          Qaytarish
        </button>
      </div>

      {/* ======= MAIN ======= */}
      <div className="voz-main">
        {/* ---- Chap kolonka ---- */}
        <div className={`voz-left${mobileTab === "action" ? " voz-hide-mobile" : ""}`}>
          <div className="voz-filter-row">
            <input
              className="voz-input voz-search-input"
              placeholder={
                mode === "returned"
                  ? "Qaytarilgan tovarlar ichidan qidiring"
                  : "Sotilgan tovarlar ichidan qidiring"
              }
              value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)}
              disabled={busy}
            />
            <div className="voz-date-range">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={busy}
              />
              <span>dan</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={busy}
              />
            </div>
            <select
              className="voz-select voz-sklad-sel"
              value={leftSklad}
              onChange={(e) => setLeftSklad(e.target.value)}
              disabled={busy}
              title="Sklad filtri"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div className="voz-table fr-card">
            <div className="voz-thead">
              <div>Mahsulot</div>
              <div>Seriya</div>
              <div>Sana</div>
              <div>Narx</div>
              <div>Soni</div>
              <div>Holati</div>
              <div>Turi</div>
            </div>

            {loadingLeft ? (
              <div className="voz-row">
                <div className="muted">Yuklanmoqda...</div>
              </div>
            ) : leftList.length === 0 ? (
              <div className="voz-row">
                <div className="muted">
                  {mode === "sold" && !seller?.id
                    ? "Avval haridor tanlang."
                    : "Ma’lumot topilmadi."}
                </div>
              </div>
            ) : (
              leftList.map((row) => {
                const picked =
                  mode === "sold" && selectedSold.find((x) => x.id === row.id);
                return (
                  <div
                    key={`${row.kind}-${row.id}`}
                    className={`voz-row ${
                      mode === "sold" ? "pickable" : ""
                    } ${picked ? "picked" : ""}`}
                    onClick={() => !busy && togglePickSold(row)}
                    title={
                      mode === "sold"
                        ? "Tanlash / olib tashlash"
                        : "Qaytarilgan ro‘yxati"
                    }
                  >
                    <div>{row.name}</div>
                    <div>{row.seria}</div>
                    <div>{row.date}</div>
                    <div>{fmtCur(row.unitPrice, row.currency || skladCurrency)}</div>
                    <div>{row.qty} dona</div>
                    <div>{row.status || "—"}</div>
                    <div>{row.type || "—"}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ---- O’ng kolonka ---- */}
        <div className={`voz-right${mobileTab === "info" ? " voz-hide-mobile" : ""}`}>
          {/* Yuqori karta: tanlangan sotilganlar */}
          <div className="voz-card fr-card">
            <h3>Qaytariladigan (sotilganlardan tanlangan)</h3>
            {selectedSold.length === 0 ? (
              <div className="muted">Hali tanlanmadi.</div>
            ) : (
              selectedSold.map((it) => (
                <div className="voz-row small editable" key={it.id}>
                  <span className="nm">{it.name}</span>
                  <span className="sr">{it.seria}</span>
                  <span className="pr">{fmtCur(it.unitPrice, it.currency || skladCurrency)}</span>

                  {/* Holat selecti */}
                  <select
                    className="voz-select small"
                    value={it.condition || ""}
                    onChange={(e) =>
                      setSelectedSold((prev) =>
                        prev.map((p) =>
                          p.id === it.id
                            ? { ...p, condition: e.target.value }
                            : p
                        )
                      )
                    }
                    disabled={busy}
                  >
                    <option value="">Holatini tanlang</option>
                    <option value="yaroqli">Yaroqli</option>
                    <option value="yaroqsiz">Yaroqsiz</option>
                  </select>

                  <div className="voz-counter">
                    <button
                      onClick={() => incSelQty(it.id, -1)}
                      disabled={toNum(it.qty) <= 0.001 || busy}
                    >
                      -
                    </button>
                    <input
                      inputMode="decimal"
                      value={it.qty}
                      onChange={(e) => setSelQtyStr(it.id, e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))}
                      onBlur={() => blurSelQty(it.id)}
                      disabled={busy}
                    />
                    <button
                      onClick={() => incSelQty(it.id, +1)}
                      disabled={toNum(it.qty) >= (it.maxQty || 1) || busy}
                    >
                      +
                    </button>
                  </div>

                  <button
                    className="voz-del"
                    onClick={() => !busy && removeSel(it.id)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}

            <div className="voz-total">
              <p>
                Maxsulot:{" "}
                {selectedSold.reduce((s, x) => s + toNum(x.qty), 0)} dona
              </p>
              <p>Jami: {fmtCur(refundTotal, skladCurrency)}</p>
            </div>
          </div>

          {/* Pastki karta: almashtirish savati (sklad filtri bilan) */}
          <div className="voz-card fr-card">
            <div className="voz-input-row">
              <input
                className="voz-input"
                placeholder="Almashtirish uchun mahsulot qidiring"
                value={qRep}
                onChange={(e) => setQRep(e.target.value)}
                disabled={busy}
              />
              <select
                className="voz-select"
                value={repWarehouse}
                onChange={(e) => setRepWarehouse(e.target.value)}
                title="Baza (sklad) filtri"
                disabled={busy}
              >
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              <select
                className="voz-select"
                defaultValue="Donaga"
                title="Narx turi (demo)"
                disabled
              >
                <option>Donaga</option>
                <option>Ulgurji</option>
              </select>
            </div>

            {repOpts.length > 0 && (
              <div className="dropdown wide">
                {repOpts.map((p) => (
                  <button key={p.id} onClick={() => !busy && addReplace(p)}>
                    <div className="line1">
                      {p.name} — {p.seria}
                    </div>
                    <div className="line2">Narx: {fmtCur(p.price, skladCurrency)}</div>
                  </button>
                ))}
              </div>
            )}

            {replaceItems.length === 0 ? (
              <div className="muted">Hali mahsulot qo‘shilmagan.</div>
            ) : (
              replaceItems.map((p) => (
                <div className="voz-row small editable" key={p.id}>
                  <span className="nm">{p.name}</span>
                  <span className="sr">{p.seria}</span>

                  <div className="voz-counter">
                    <button
                      onClick={() => changeRepQty(p.id, -1)}
                      disabled={toNum(p.qty) <= 0.001 || busy}
                    >
                      -
                    </button>
                    <input
                      inputMode="decimal"
                      value={p.qty}
                      onChange={(e) => setRepQtyStr(p.id, e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))}
                      onBlur={() => blurRepQty(p.id)}
                      disabled={busy}
                    />
                    <button
                      onClick={() => changeRepQty(p.id, +1)}
                      disabled={busy}
                    >
                      +
                    </button>
                  </div>

                  <div className="price-edit">
                    <input
                      className="price"
                      value={p.price}
                      onChange={(e) => changeRepPrice(p.id, e.target.value)}
                      inputMode="decimal"
                      placeholder="0.000"
                      disabled={busy}
                    />
                    <span className="usd">so'm</span>
                  </div>

                  <button
                    className="voz-del"
                    onClick={() => !busy && removeRep(p.id)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}

            <div className="voz-total">
              <p>
                Maxsulot:{" "}
                {replaceItems.reduce((s, x) => s + toNum(x.qty), 0)} dona
              </p>
              <p>Jami: {fmtCur(replaceTotal, skladCurrency)}</p>
            </div>

            {selectedSold.length > 0 && (
              <div className="voz-settle">
                <div className="voz-settle-row">
                  <div className="voz-diff">
                    {diffTotal > 0
                      ? `Mijozga qaytariladi: ${fmtCur(diffTotal, skladCurrency)}`
                      : diffTotal < 0
                      ? `Mijoz to‘laydi: ${fmtCur(Math.abs(diffTotal), skladCurrency)}`
                      : `Farq: ${fmtCur(0, skladCurrency)}`}
                  </div>
                  <div className="voz-settle-opts">
                    <label
                      className={`settle-opt ${
                        settleMethod === "cash" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="voz-settle"
                        value="cash"
                        checked={settleMethod === "cash"}
                        onChange={() => setSettleMethod("cash")}
                        disabled={busy}
                      />
                      <span>
                        {diffTotal >= 0 ? "Kassadan berish" : "Kassaga tushadi"}
                      </span>
                    </label>
                    <label
                      className={`settle-opt ${
                        settleMethod === "balance" ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="voz-settle"
                        value="balance"
                        checked={settleMethod === "balance"}
                        onChange={() => setSettleMethod("balance")}
                        disabled={busy}
                      />
                      <span>
                        {diffTotal >= 0
                          ? "Balansga qo‘shish"
                          : "Balansga yozish"}
                      </span>
                    </label>
                  </div>
                </div>
                {settleMethod === "cash" && diffTotal > 0 && (
                  <div className="voz-cash-opts">
                    <div className="cash-title">Kassadan qaytarish turi</div>
                    <div className="cash-buttons">
                      <button
                        className={`cash-btn ${
                          cashRefundType === "cash_som" ? "active" : ""
                        }`}
                        onClick={() => setCashRefundType("cash_som")}
                        type="button"
                        disabled={busy}
                      >
                        {skladCurrency === "usd" ? "Naqd $ dan" : "Naqd so'mdan"}
                      </button>
                      {skladCurrency !== "usd" && (
                        <>
                      <button
                        className={`cash-btn ${
                          cashRefundType === "card_som" ? "active" : ""
                        }`}
                        onClick={() => setCashRefundType("card_som")}
                        type="button"
                        disabled={busy}
                      >
                        Karta so'mdan
                      </button>
                      <button
                        className={`cash-btn ${
                          cashRefundType === "terminal_som" ? "active" : ""
                        }`}
                        onClick={() => setCashRefundType("terminal_som")}
                        type="button"
                        disabled={busy}
                      >
                        Terminal so'mdan
                      </button>
                        </>
                      )}
                    </div>
                    {cashRefundType === "cash_som" && (
                      <div className="cash-info">
                        Qaytarish: {fmtCur(refundAmt, skladCurrency)}
                      </div>
                    )}
                    {cashRefundType === "card_som" && (
                      <div className="cash-info">
                        Qaytarish: {fmtCur(refundAmt, skladCurrency)}
                      </div>
                    )}
                    {cashRefundType === "terminal_som" && (
                      <div className="cash-info">
                        Qaytarish: {fmtCur(refundAmt, skladCurrency)}
                      </div>
                    )}
                  </div>
                )}
                {settleMethod === "cash" && diffTotal < 0 && (
                  <div className="voz-cash-opts">
                    <div className="cash-title">Kassaga tushish turi</div>
                    <div className="cash-buttons">
                      <button
                        className={`cash-btn ${
                          cashInType === "cash_som" ? "active" : ""
                        }`}
                        onClick={() => setCashInType("cash_som")}
                        type="button"
                        disabled={busy}
                      >
                        {skladCurrency === "usd" ? "Naqd $ ga" : "Naqd so'mga"}
                      </button>
                      {skladCurrency !== "usd" && (
                        <>
                      <button
                        className={`cash-btn ${
                          cashInType === "card_som" ? "active" : ""
                        }`}
                        onClick={() => setCashInType("card_som")}
                        type="button"
                        disabled={busy}
                      >
                        Karta so'mga
                      </button>
                      <button
                        className={`cash-btn ${
                          cashInType === "terminal_som" ? "active" : ""
                        }`}
                        onClick={() => setCashInType("terminal_som")}
                        type="button"
                        disabled={busy}
                      >
                        Terminal so'mga
                      </button>
                        </>
                      )}
                    </div>
                    {cashInType === "cash_som" && (
                      <div className="cash-info">
                        Kassaga tushadi: {fmtCur(payAmt, skladCurrency)}
                      </div>
                    )}
                    {cashInType === "card_som" && (
                      <div className="cash-info">
                        Kassaga tushadi: {fmtCur(payAmt, skladCurrency)}
                      </div>
                    )}
                    {cashInType === "terminal_som" && (
                      <div className="cash-info">
                        Kassaga tushadi: {fmtCur(payAmt, skladCurrency)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="voz-actions">
              <button
                className={`voz-btn orange ${!canReturn ? "disabled" : ""}`}
                onClick={doReturn}
                disabled={!canReturn || busy}
              >
                Qaytarish
              </button>
              <button
                className={`voz-btn yellow ${!canExchange ? "disabled" : ""}`}
                onClick={doExchange}
                disabled={!canExchange || busy}
              >
                Almashtirish
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
